import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import MasterModalShell from "./common/MasterModalShell";
import CodeField from "./common/CodeField";
import {
  validateCode,
  validateExternalCodes,
  validateRequired,
} from "./common/validation";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast } from "react-toastify";
import {
  administrationRouteApi,
  administrationSiteApi,
  antigenApi,
  presentationApi,
  vaccineProductApi,
} from "api/immunization";
import {
  GST_PERCENTS,
  VACCINE_NATURES,
  humanise,
} from "config/immunizationConstants";
import ExternalCodesField from "./common/ExternalCodesField";

const Section = ({ title, children }) => (
  <Box>
    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
      {title}
    </Typography>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {children}
    </Box>
  </Box>
);

const VaccineProductModal = ({ open, onClose, data }) => {
  const productId = data?._id;
  const isUpdate = Boolean(productId);

  const [form, setForm] = useState({});
  const [antigens, setAntigens] = useState([]);
  const [presentations, setPresentations] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [sites, setSites] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      code: data?.code || "",
      brandName: data?.brandName || "",
      genericName: data?.genericName || "",
      manufacturer: data?.manufacturer || "",
      composition: (data?.composition || []).map((entry) => ({
        antigenId:
          typeof entry.antigenId === "object" ?
            entry.antigenId?._id
          : entry.antigenId,
        antigenCode: entry.antigenCode || "",
        strain: entry.strain || "",
        valency: entry.valency ?? "",
      })),
      nature: data?.nature || "INACTIVATED",
      isLiveAttenuated: Boolean(data?.isLiveAttenuated),
      presentationCode: data?.presentationCode || "",
      dosesPerVial: data?.dosesPerVial ?? 1,
      requiresDiluent: Boolean(data?.requiresDiluent),
      defaultRouteCode: data?.defaultRouteCode || "",
      defaultSiteCode: data?.defaultSiteCode || "",
      defaultDoseVolumeMl: data?.defaultDoseVolumeMl ?? "",
      storageTempMinC: data?.storageTempMinC ?? "",
      storageTempMaxC: data?.storageTempMaxC ?? "",
      freezeSensitive: Boolean(data?.freezeSensitive),
      lightSensitive: Boolean(data?.lightSensitive),
      openVialHours: data?.openVialHours ?? "",
      externalCodes: data?.externalCodes || [],
      hsnCode: data?.hsnCode || "",
      gstPercent: data?.gstPercent ?? "",
      sequence: data?.sequence ?? 0,
    });
  }, [open, data]);

  useEffect(() => {
    if (!open) return;

    antigenApi
      .active()
      .then((res) => setAntigens(res?.data?.data || []))
      .catch(() => toast.error("Could not load antigens"));
    presentationApi
      .active()
      .then((res) => setPresentations(res?.data?.data || []))
      .catch(() => toast.error("Could not load presentations"));
    administrationRouteApi
      .active()
      .then((res) => setRoutes(res?.data?.data || []))
      .catch(() => toast.error("Could not load administration routes"));
  }, [open]);

  useEffect(() => {
    if (!open) return;

    administrationSiteApi
      .active(form.defaultRouteCode ? { routeCode: form.defaultRouteCode } : {})
      .then((res) => setSites(res?.data?.data || []))
      .catch(() => toast.error("Could not load administration sites"));
  }, [open, form.defaultRouteCode]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const composition = form.composition || [];
  const updateComposition = (index, patch) =>
    set({
      composition: composition.map((row, i) =>
        i === index ? { ...row, ...patch } : row,
      ),
    });

  const validationError = useMemo(() => {
    const base =
      validateCode(form.code, { isUpdate, label: "Product code" }) ||
      validateRequired(form.brandName, "Brand name") ||
      validateRequired(form.presentationCode, "Presentation") ||
      validateExternalCodes(form.externalCodes);
    if (base) return base;

    if (composition.length === 0) {
      return "A vaccine product must contain at least one antigen";
    }
    if (composition.some((row) => !row.antigenId)) {
      return "Every composition row needs an antigen";
    }
    const ids = composition.map((row) => row.antigenId);
    if (new Set(ids).size !== ids.length) {
      return "The same antigen cannot appear twice in the composition";
    }

    if (form.nature === "LIVE_ATTENUATED" && !form.isLiveAttenuated) {
      return "A live-attenuated product must be flagged as live attenuated";
    }
    if (form.defaultSiteCode && !form.defaultRouteCode) {
      return "A default site cannot be set without a default route";
    }
    if (
      form.storageTempMinC !== "" &&
      form.storageTempMaxC !== "" &&
      Number(form.storageTempMaxC) < Number(form.storageTempMinC)
    ) {
      return "Storage maximum temperature must not be below the minimum";
    }

    return "";
  }, [form, composition, isUpdate]);

  const handleSubmit = async () => {
    if (validationError) return;
    setSaving(true);

    const numberOrNull = (value) => (value === "" ? null : Number(value));

    const payload = {
      brandName: form.brandName.trim(),
      genericName: form.genericName?.trim() || "",
      manufacturer: form.manufacturer?.trim() || "",
      composition: composition.map((row) => ({
        antigenId: row.antigenId,
        antigenCode: row.antigenCode,
        ...(row.strain?.trim() ? { strain: row.strain.trim() } : {}),
        valency: row.valency === "" ? null : Number(row.valency),
      })),
      nature: form.nature,
      isLiveAttenuated: form.isLiveAttenuated,
      presentationCode: form.presentationCode,
      dosesPerVial: Number(form.dosesPerVial) || 1,
      requiresDiluent: form.requiresDiluent,
      defaultRouteCode: form.defaultRouteCode || "",
      defaultSiteCode: form.defaultSiteCode || "",
      defaultDoseVolumeMl: numberOrNull(form.defaultDoseVolumeMl),
      storageTempMinC: numberOrNull(form.storageTempMinC),
      storageTempMaxC: numberOrNull(form.storageTempMaxC),
      freezeSensitive: form.freezeSensitive,
      lightSensitive: form.lightSensitive,
      openVialHours: numberOrNull(form.openVialHours),
      externalCodes: form.externalCodes,
      hsnCode: form.hsnCode?.trim() || "",
      gstPercent: form.gstPercent === "" ? null : Number(form.gstPercent),
      sequence: Number(form.sequence) || 0,
    };

    try {
      if (isUpdate) {
        await vaccineProductApi.update(productId, payload);
        toast.success("Vaccine product updated successfully");
      } else {
        await vaccineProductApi.create({ ...payload, code: form.code.trim() });
        toast.success("Vaccine product created successfully");
      }
      onClose("save");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to ${isUpdate ? "update" : "create"} vaccine product`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <MasterModalShell
      open={open}
      onClose={onClose}
      isUpdate={isUpdate}
      entityLabel="Vaccine Product"
      saving={saving}
      validationError={validationError}
      onSubmit={handleSubmit}
      maxWidth="md"
    >
      <Section title="Identity">
        <Box sx={{ display: "flex", gap: 2 }}>
          <CodeField
            value={form.code}
            onChange={(value) => set({ code: value })}
            isUpdate={isUpdate}
            label="Product Code"
          />
          <TextField
            label="Brand Name"
            required
            fullWidth
            size="small"
            value={form.brandName || ""}
            onChange={(event) => set({ brandName: event.target.value })}
          />
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="Generic Name"
            fullWidth
            size="small"
            value={form.genericName || ""}
            onChange={(event) => set({ genericName: event.target.value })}
          />
          <TextField
            label="Manufacturer"
            fullWidth
            size="small"
            value={form.manufacturer || ""}
            onChange={(event) => set({ manufacturer: event.target.value })}
          />
        </Box>
      </Section>

      <Divider />

      <Section title="Antigen Composition">
        <Typography variant="caption" color="text.secondary">
          One administration of this product satisfies every antigen listed
          here. This is what makes combination vaccines count correctly in
          coverage reporting.
        </Typography>

        {composition.map((row, index) => (
          <Box
            key={index}
            sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}
          >
            <Autocomplete
              size="small"
              sx={{ flex: 2 }}
              options={antigens}
              value={antigens.find((a) => a._id === row.antigenId) || null}
              getOptionLabel={(option) => option?.antigenName || ""}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              onChange={(event, next) =>
                updateComposition(index, {
                  antigenId: next?._id || "",
                  antigenCode: next?.code || "",
                })
              }
              renderInput={(params) => (
                <TextField {...params} label="Antigen" required />
              )}
            />
            <TextField
              size="small"
              label="Code"
              sx={{ minWidth: 150 }}
              value={row.antigenCode || ""}
              InputProps={{ readOnly: true }}
            />
            <TextField
              size="small"
              label="Strain"
              sx={{ minWidth: 130 }}
              value={row.strain || ""}
              onChange={(event) =>
                updateComposition(index, { strain: event.target.value })
              }
            />
            <TextField
              size="small"
              label="Valency"
              type="number"
              sx={{ minWidth: 100 }}
              value={row.valency}
              onChange={(event) =>
                updateComposition(index, { valency: event.target.value })
              }
            />
            <IconButton
              onClick={() =>
                set({
                  composition: composition.filter((_, i) => i !== index),
                })
              }
            >
              <DeleteOutlineIcon fontSize="small" color="error" />
            </IconButton>
          </Box>
        ))}

        <Box>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() =>
              set({
                composition: [
                  ...composition,
                  { antigenId: "", antigenCode: "", strain: "", valency: "" },
                ],
              })
            }
          >
            Add antigen
          </Button>
        </Box>
      </Section>

      <Divider />

      <Section title="Presentation & Administration">
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            select
            label="Nature"
            size="small"
            fullWidth
            value={form.nature || "INACTIVATED"}
            onChange={(event) => {
              const nature = event.target.value;
              set({
                nature,
                ...(nature === "LIVE_ATTENUATED" ?
                  { isLiveAttenuated: true }
                : {}),
              });
            }}
          >
            {VACCINE_NATURES.map((nature) => (
              <MenuItem key={nature} value={nature}>
                {humanise(nature)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Presentation"
            required
            size="small"
            fullWidth
            value={form.presentationCode || ""}
            onChange={(event) => set({ presentationCode: event.target.value })}
          >
            {presentations.map((item) => (
              <MenuItem key={item.code} value={item.code}>
                {item.display}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Doses / Vial"
            type="number"
            size="small"
            sx={{ minWidth: 130 }}
            value={form.dosesPerVial}
            onChange={(event) => set({ dosesPerVial: event.target.value })}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            select
            label="Default Route"
            size="small"
            fullWidth
            value={form.defaultRouteCode || ""}
            onChange={(event) =>
              set({ defaultRouteCode: event.target.value, defaultSiteCode: "" })
            }
          >
            <MenuItem value="">None</MenuItem>
            {routes.map((item) => (
              <MenuItem key={item.code} value={item.code}>
                {item.display}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Default Site"
            size="small"
            fullWidth
            disabled={!form.defaultRouteCode}
            value={form.defaultSiteCode || ""}
            onChange={(event) => set({ defaultSiteCode: event.target.value })}
            helperText={
              form.defaultRouteCode ?
                "Only sites valid for the chosen route"
              : "Choose a route first"
            }
          >
            <MenuItem value="">None</MenuItem>
            {sites.map((item) => (
              <MenuItem key={item.code} value={item.code}>
                {item.display}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Dose Volume (ml)"
            type="number"
            size="small"
            sx={{ minWidth: 150 }}
            value={form.defaultDoseVolumeMl}
            onChange={(event) =>
              set({ defaultDoseVolumeMl: event.target.value })
            }
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(form.isLiveAttenuated)}
                disabled={form.nature === "LIVE_ATTENUATED"}
                onChange={(event) =>
                  set({ isLiveAttenuated: event.target.checked })
                }
              />
            }
            label="Live attenuated"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(form.requiresDiluent)}
                onChange={(event) =>
                  set({ requiresDiluent: event.target.checked })
                }
              />
            }
            label="Requires diluent"
          />
        </Box>
      </Section>

      <Divider />

      <Section title="Storage (descriptive — cold chain arrives in a later phase)">
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <TextField
            label="Storage Min (°C)"
            type="number"
            size="small"
            sx={{ minWidth: 150 }}
            value={form.storageTempMinC}
            onChange={(event) => set({ storageTempMinC: event.target.value })}
          />
          <TextField
            label="Storage Max (°C)"
            type="number"
            size="small"
            sx={{ minWidth: 150 }}
            value={form.storageTempMaxC}
            onChange={(event) => set({ storageTempMaxC: event.target.value })}
          />
          <TextField
            label="Open Vial Hours"
            type="number"
            size="small"
            sx={{ minWidth: 150 }}
            value={form.openVialHours}
            onChange={(event) => set({ openVialHours: event.target.value })}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(form.freezeSensitive)}
                onChange={(event) =>
                  set({ freezeSensitive: event.target.checked })
                }
              />
            }
            label="Freeze sensitive"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(form.lightSensitive)}
                onChange={(event) =>
                  set({ lightSensitive: event.target.checked })
                }
              />
            }
            label="Light sensitive"
          />
        </Box>
      </Section>

      <Divider />

      <Section title="Classification & Coding">
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="HSN Code"
            size="small"
            fullWidth
            value={form.hsnCode || ""}
            onChange={(event) => set({ hsnCode: event.target.value })}
          />
          <TextField
            select
            label="GST %"
            size="small"
            sx={{ minWidth: 130 }}
            value={form.gstPercent}
            onChange={(event) => set({ gstPercent: event.target.value })}
          >
            <MenuItem value="">Not set</MenuItem>
            {GST_PERCENTS.map((rate) => (
              <MenuItem key={rate} value={rate}>
                {rate}%
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Sequence"
            type="number"
            size="small"
            sx={{ minWidth: 120 }}
            value={form.sequence}
            onChange={(event) => set({ sequence: event.target.value })}
          />
        </Box>

        <Typography variant="caption" color="text.secondary">
          Selling price is set per hospital and is not part of the global
          catalogue.
        </Typography>

        <ExternalCodesField
          value={form.externalCodes}
          onChange={(next) => set({ externalCodes: next })}
          disabled={saving}
        />
      </Section>
    </MasterModalShell>
  );
};

export default VaccineProductModal;
